import {useMemo} from 'react'
import RouteMap from '@/components/map/RouteMap.jsx'
import {metresBetween, toPoint} from '@/utils/polyline'
import {INDOOR_STYLE_CONFIG, ROUTE_SLOT, STANDARD_STYLE_URL} from '@/components/map/standardStyle.js'

/**
 * THE PASSENGER'S LIVE MAP — the driver portal's `EnRouteMap`, minus the two controls that
 * belong to whoever is holding the wheel.
 *
 * ── What is the same, and why that matters ────────────────────────────────────────
 * The car, the solved route, the following camera, the map-matching, the trail, the
 * instruction banner: all of it is `RouteMap`, byte for byte the driver's component. Two
 * separate map implementations of the same journey is how the two products start
 * disagreeing about where a car is — and when a passenger and a driver are on the phone to
 * each other, they are looking at the same road.
 *
 * ── ⚠️ WHAT IS GONE, AND WHY ──────────────────────────────────
 * The voice button. Guidance is a driver's control over their own navigation and has no
 * owner here: the passenger is a passenger. `useVoiceGuidance` is not imported at all
 * rather than imported and left muted — a hook that can never be switched on is a hook
 * that will be wondered about.
 *
 * ── ⚠️ AND THE 2D/3D TOGGLE WENT WITH IT ─────────────────────────────
 * It was added when this map moved to the Standard style, on the reasoning that a tilted
 * view is how you read an airport interior. That reasoning was right and the CONTROL was
 * wrong: a passenger has no use for a tilted view of a road, and two buttons that both
 * tilt the map is one button too many.
 *
 * So this map is flat and north-up throughout, with nothing to change that.
 *
 * ⚠️ THE INDOOR VIEW WENT THE SAME WAY (2026-09-12) — an eye-level camera inside a
 * terminal, reached from an Indoor button. The arrival flow answers "where am I in this
 * building" with a surveyed walk and photographs of the real doorways, which beats a
 * tilted camera over a floor plan; two competing ways to do it on one screen is one too
 * many. `RouteMap` still swaps its flat car sprite for the 3D model past 12° of tilt,
 * but nothing on this map tilts now, so that chunk is never fetched here.
 */

/**
 * How close a turn has to be before the camera tightens onto it, in metres, and the two
 * zooms either side.
 *
 * Taken from the driver's map unchanged. A passenger has no turn to take, but the camera
 * motion is what makes the map read as live rather than as a picture of a car — and the
 * approach to a junction is exactly when a passenger watching the last minute of the
 * approach wants to see which side of the road the car is on.
 */
const CRUISE_ZOOM = 18.5
const JUNCTION_ZOOM = 19.4
const JUNCTION_METERS = 150

/**
 * ⚠️ THE ROUTE IS SOLVED BY THE PAGE, NOT HERE, and passed in.
 *
 * The obvious shape is for the map to own `useManifestRoute` the way the driver's does.
 * It cannot here, because the ETA in this page's header — "Arriving in 10 min" — is the
 * same solve. Calling the hook in both places would put two Directions requests on the
 * wire per re-solve and let the header and the map disagree about the arrival time by one
 * request. One solve, in `useTrackedRide`, read by both.
 *
 * @param {object} driver     `{lat, lng, heading}` from the socket — see `useDriverPosition`
 * @param {Array}  drops      every location on the ride, in order, from `buildManifest`
 * @param {object} route      the solved run, from `useTrackedRide`
 * @param {object} fitPadding framing inset, supplied by whatever is hosting the map
 * @param {object} me         the PASSENGER's own position, from `useMyLocation`
 * @param {string} bookedPolyline the ride's own stored geometry, for before the car appears
 * @param {boolean} showBanner full-screen (navigation) view — tighter camera, indoor control
 */
export default function TrackDriverMap({driver, drops = [], route, me = null, bookedPolyline = null, fitPadding, showBanner = false}) {
    /*
     * ⚠️ THERE ARE TWO MAPS HERE, AND THE SWITCH IS WHETHER A CAR EXISTS YET.
     *
     * Nothing hands a passenger the driver's last known position — the trip resource has no
     * coordinates on it and no endpoint returns any — so the only positions that exist are
     * the ones broadcast while this page is open. Before the first one lands there is
     * genuinely no car to draw.
     *
     * That used to put a grey panel and an apology where the map goes, which stopped the
     * page dead at exactly the moment someone had opened it to look at something. So the
     * map draws the RIDE instead: the booked route, the pickup, the stops and the
     * destination, framed to fit. A real map of a real journey, missing only the vehicle.
     * The car and the live line drop in on the first ping and the camera takes over.
     */
    const maneuver = route?.maneuver

    /*
     * HOW FAR THE TURN IS RIGHT NOW — measured to the maneuver's own point on every frame
     * rather than read off `maneuver.distanceText`, which is frozen at the moment the route
     * was solved and only refreshes every 120m. Left alone it holds one number for a whole
     * block and then jumps, which is a countdown that does not count down.
     */
    /* ⚠️ THE FOUR NUMBERS ARE PULLED OUT FIRST, and not for tidiness. Closing over the
       OBJECTS would make this recompute whenever either identity changed — and a position
       frame is a new object every few seconds even when the car has not moved a metre.
       Reading the primitives is also what lets the React Compiler infer the same
       dependencies this declares; given the objects, it infers `driver` and gives up on
       optimising the component entirely. */
    const driverLat = driver?.lat
    const driverLng = driver?.lng
    const turnLat = maneuver?.point?.lat
    const turnLng = maneuver?.point?.lng
    const toManeuver = useMemo(() => {
        const here = toPoint({lat: driverLat, lng: driverLng})
        const at = toPoint({lat: turnLat, lng: turnLng})
        return here && at ? metresBetween(here, at) : null
    }, [driverLat, driverLng, turnLat, turnLng])

    const navZoom = toManeuver != null && toManeuver < JUNCTION_METERS ? JUNCTION_ZOOM : CRUISE_ZOOM

    /*
     * ⚠️ MEMOISED ON THE DROPS. `RouteMap` rebuilds every marker when this array changes
     * identity, and the car's position republishes every few seconds.
     *
     * ⚠️ AND THE FIRST LOCATION CHANGES ROLE WITH THE CAR. With a driver on the map the car
     * IS the start of the journey, so every ride location is a waypoint ahead of it.
     * Without one the pickup is the start, and it becomes the origin marker rather than
     * being drawn as another stop along the way.
     */
    /*
     * ⚠️ THE CARD IS AN OVERVIEW, NOT A SMALL NAVIGATION MAP.
     *
     * Full screen answers "where is my car right now" and earns a camera that follows it.
     * The card answers "what is this journey" — and a 320px window running the same nav
     * camera showed a car on one street with the ride it belongs to entirely off the edge,
     * which is a picture of nothing anyone asked about. Worse, it is not pannable: the
     * whole card is a button that expands the map, so a passenger could not look around to
     * find the rest of their trip.
     *
     * So the card draws the BOOKED route, every stop pinned, fitted to the box. No car, no
     * person pin, no camera — those are questions the full-screen map exists to answer.
     */
    const overview = !showBanner

    const {origin, stopOvers, destination} = useMemo(() => {
        const points = drops.map((d) => d.point)
        return {
            origin: points[0] || null,
            /**
             * ⚠️ FROM INDEX 1, ALWAYS — THE PICKUP IS THE ORIGIN, NOT A STOP.
             *
             * The pickup gets the round `trip_origin` start marker, the same one the trip
             * details map uses — a stop pin on it read as "this is a stopover". It is left
             * out of this list whether or not a car is on the map, so the pickup has exactly
             * one marker: the old "dot stacked on a pin" came from it being in BOTH.
             */
            stopOvers: points.slice(1, -1),
            destination: points[points.length - 1] || null,
        }
    }, [drops])

    return (
        <>
            <RouteMap
                /* The car stays on the tarmac — the same rule the driver's own portal
                   follows, matched against the whole road network rather than the route. */
                snapToRoads
                /*
                 * THE LIVE RUN once there is a car, the BOOKED ride before that.
                 *
                 * ⚠️ THE DRIVER'S MAP REFUSES THIS FALLBACK AND IS RIGHT TO. There, the
                 * stored polyline is the ride as booked drawn from a place the driver is
                 * not at — worse than an empty map, because it looks correct while showing
                 * a route nobody is on. A passenger is not being navigated: the booked
                 * route is simply their own journey, which is a true and useful thing to
                 * look at while the car has yet to appear on it.
                 */
                /* ⚠️ THE BOOKED ROUTE, ALWAYS — the whole journey end to end.
                   `route.polyline` is the leg from wherever the car is NOW, which is the
                   driver's question, not the passenger's: it redraws as the car moves and
                   never shows the trip they actually booked. */
                /* ⚠️ THE BOOKED POLYLINE AND NOTHING ELSE — no `|| route?.polyline`.
                   That fallback is the live run solved FROM THE CAR'S POSITION, so whenever
                   the trip carried no stored geometry the line began at the vehicle and the
                   car sat on its end, joined to the route by a segment that is not part of the
                   ride. The trip-details map draws `trip.route.polyline` alone; so does this.
                   The car is a marker on the map, never a vertex of the line. */
                polyline={bookedPolyline}
                /* ⚠️ THE RIDE'S OWN START, NOT THE CAR. Handing origin to the driver made
                   the route begin wherever the vehicle happened to be, so the first half of
                   the journey vanished from the map as it was driven. */
                origin={origin}
                stopOvers={stopOvers}
                destination={destination}
                /* ⚠️ NO `legs` PROP. RouteMap treats legs as outranking origin/destination
                   and would re-derive the ride's endpoints from whatever shape Directions
                   returned, handing ownership of the markers to the response. */
                /* ⚠️ NO CAR AND NO FOLLOW ON THE OVERVIEW. Both are what turn this into a
                   navigation map: the car pulls the camera and the follow keeps it there. */
                driver={overview ? null : driver}
                /* ⚠️ NO FOLLOW, NO NAV CAMERA, NO CAMERA FOCUS — see the note above the
                   RouteMap call. The map is fitted to the route once and left alone. */
                follow={null}
                showTrail={false}
                /* ⚠️ THE WHOLE TRIP, NOT WHAT IS LEFT OF IT. The default cuts the line at
                   the car, which is the driver's question — the road behind them is not
                   something they can act on. A passenger is looking at their own journey:
                   trimmed, it shrank as it was driven until the last minute of the ride was
                   a stub of green beside a car, with the pickup they were collected from
                   and the road already covered both gone. This draws the ride the way the
                   trip-details map draws it, with the car and their own pin on top. */
                trimBehind={false}
                /*
                 * ⚠️ THE CAMERA SITS ON THE PASSENGER, NOT THE CAR — the one thing on this
                 * map that differs from the driver's. They are watching a vehicle come to
                 * where they are standing, so their own position is the fixed point and the
                 * car drives across the frame toward it. Riding with the car instead turns
                 * the map into a chase and slides the kerb they are standing on off screen.
                 *
                 * Null when the browser has not given a position — declined, blocked, or
                 * still working it out — and the camera then follows the driver, which is
                 * the old behaviour and still useful.
                 */
                cameraFocus={null}
                /* Their own pin, on their own map. The driver portal gates this to airport
                   pickups because there it is a SECOND person's location cluttering a map
                   read at speed; here it is the viewer, and a map centred on you with
                   nothing marking you is a map centred on nothing. */
                /* Their own pin belongs on the map that is about where they are standing,
                   not on a thumbnail of the whole journey. */
                passenger={overview ? null : me}
                /*
                 * ⚠️ THE NAV CAMERA ARRIVES WITH THE CAR, AND NOT BEFORE.
                 *
                 * `driverFocus` means "wait for a fix rather than frame anything else",
                 * which is right for a driver whose GPS is warming up and wrong here: with
                 * no fix ever guaranteed, it would hold the map's default view of Toronto
                 * instead of showing the ride. Off, `RouteMap` fits the route's bounds,
                 * which is exactly the map a waiting passenger wants.
                 *
                 * ⚠️ `navPitch` IS LEFT AT ITS DEFAULT OF 0, and nothing on this map
                 * changes it. It is flat and north-up in every state — the indoor view was
                 * the one exception and it is gone; see the note at the top of this file.
                 */
                /* ⚠️ `driverFocus`, `navCamera` and `navZoom` ALL REMOVED. Together they
                   were what made this a navigation map — framing on the car, tightening
                   into turns, riding along with it. A passenger is not navigating: they
                   want to see the trip, the car on it and themselves. RouteMap's default
                   is to fit the route's bounds, which is exactly that. */
                /*
                 * ── ⚠️ STANDARD, NOT THE CONFIG'S `light-v10` ───────────────────
                 *
                 * AIRPORT INTERIORS, and they are only available here. `showIndoor` is a
                 * configuration property of the Standard style; classic styles have no
                 * config surface at all, so there is no version of this that keeps
                 * `light-v10`. A passenger waiting inside a terminal sees the floor plan,
                 * the indoor labels and Mapbox's floor selector rather than a roof.
                 *
                 * ⚠️ AND IT MATCHES THE DRIVER'S EN-ROUTE MAP DELIBERATELY. When the two
                 * are on the phone to each other they are looking at the same building,
                 * and one of them seeing a floor plan while the other sees a grey block
                 * is the kind of disagreement that gets a driver sent to the wrong door.
                 * The style, the config and the slot are one shared module for that
                 * reason — see `standardStyle.js`.
                 */
                styleUrlOverride={STANDARD_STYLE_URL}
                styleConfig={INDOOR_STYLE_CONFIG}
                /* Standard's layers cannot be reordered, so ours need an insertion point.
                   `top` puts the route and the car above the indoor floor plan. */
                layerSlot={ROUTE_SLOT}
                /* Floor numbers on a preview nobody can press are decoration — the whole
                   card is a button that expands the map. */
                indoorControl={showBanner}
                animate
                fitPadding={fitPadding}
                radiusClass=""
                className="absolute inset-0"
            />
        </>
    )
}

export {TrackDriverMap}
